/**
 * Admin API
 *
 * Handles all admin-related API calls (organizations, provider credentials, health).
 * Uses Zod validation for all endpoints (sensitive data is HIGH RISK).
 */

import { BaseApiClient } from '../base';
import { API_ENDPOINTS } from '../endpoints';
import { PROCESSING_PAUSED_REFUSALS } from '../processing-paused';
import {
  adminOrganizationsResponseSchema,
  adminOrgCreditsResponseSchema,
  adminProviderCredentialsResponseSchema,
  adminProviderCredentialResponseSchema,
  healthFailedDeliveriesResponseSchema,
  healthIntegrationsResponseSchema,
  healthDeliveryRetryResponseSchema,
  retentionImpactResponseSchema,
} from '@aica/api-types/schemas';
import type { RetentionImpactResponse } from '@aica/api-types';
import type {
  AdminOrganizationsResponse,
  AdminOrgCredits,
  AdminProviderCredentialsResponse,
  AdminProviderCredential,
  HealthFailedDeliveriesResponse,
  HealthIntegrationsResponse,
  HealthDeliveryRetryResponse,
  SuccessResponse,
  PlatformSetting,
  PlatformDefaults,
  KnownProviders,
  KnownModels,
} from '../types';

export class AdminApi extends BaseApiClient {
  // ============================================================================
  // ORGANIZATIONS
  // ============================================================================

  /**
   * Get admin organizations - validated with Zod
   */
  async getAdminOrganizations(params?: {
    limit?: number;
    offset?: number;
  }): Promise<AdminOrganizationsResponse> {
    const query = params
      ? `?${new URLSearchParams(params as unknown as Record<string, string>).toString()}`
      : '';
    return this.validatedRequest(
      `${API_ENDPOINTS.ADMIN_ORGANIZATIONS}${query}`,
      adminOrganizationsResponseSchema
    );
  }

  /**
   * Get admin organization credits - validated with Zod
   */
  async getAdminOrganizationCredits(orgId: string): Promise<AdminOrgCredits> {
    return this.validatedRequest(
      API_ENDPOINTS.ADMIN_ORGANIZATIONS_CREDITS(orgId),
      adminOrgCreditsResponseSchema
    );
  }

  /**
   * Add credits - POST request (no validation needed for response)
   */
  async addAdminOrganizationCredits(
    orgId: string,
    data: {
      amount: number;
      currency: string;
      source: 'prepaid' | 'invoice' | 'promo' | 'adjustment';
      description: string;
    }
  ): Promise<SuccessResponse> {
    return this.request<SuccessResponse>(
      API_ENDPOINTS.ADMIN_ORGANIZATIONS_CREDITS(orgId),
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  }

  // ============================================================================
  // PROVIDER CREDENTIALS
  // ============================================================================

  /**
   * Get admin provider credentials - validated with Zod
   */
  async getAdminProviderCredentials(): Promise<AdminProviderCredentialsResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.ADMIN_PROVIDER_CREDENTIALS,
      adminProviderCredentialsResponseSchema
    );
  }

  /**
   * Create credential - POST request
   */
  async createAdminProviderCredential(data: {
    providerType: string;
    providerName: string;
    credentialsJson: string;
    isActive: boolean;
  }): Promise<SuccessResponse> {
    return this.request<SuccessResponse>(API_ENDPOINTS.ADMIN_PROVIDER_CREDENTIALS, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Get credential by type - validated with Zod
   */
  async getAdminProviderCredentialByType(
    type: string,
    name: string
  ): Promise<{ data: AdminProviderCredential }> {
    return this.validatedRequest(
      API_ENDPOINTS.ADMIN_PROVIDER_CREDENTIALS_BY_TYPE(type, name),
      adminProviderCredentialResponseSchema
    );
  }

  /**
   * Update credential - PUT request
   */
  async updateAdminProviderCredential(
    type: string,
    name: string,
    data: { value: string }
  ): Promise<{ credential: AdminProviderCredential }> {
    return this.request<{ credential: AdminProviderCredential }>(
      API_ENDPOINTS.ADMIN_PROVIDER_CREDENTIALS_BY_TYPE(type, name),
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    );
  }

  /**
   * Delete credential - DELETE request
   */
  async deleteAdminProviderCredential(
    type: string,
    name: string
  ): Promise<SuccessResponse> {
    return this.request<SuccessResponse>(
      API_ENDPOINTS.ADMIN_PROVIDER_CREDENTIALS_BY_TYPE(type, name),
      {
        method: 'DELETE',
      }
    );
  }

  // ============================================================================
  // PLATFORM SETTINGS
  // ============================================================================

  /**
   * Get all platform settings
   */
  async getPlatformSettings(): Promise<{
    data: PlatformSetting[];
    meta: { total: number; knownProviders: KnownProviders; knownModels: KnownModels };
  }> {
    return this.request(API_ENDPOINTS.ADMIN_PLATFORM_SETTINGS);
  }

  /**
   * Get platform defaults (ASR/LLM provider settings)
   */
  async getPlatformDefaults(): Promise<{
    data: PlatformDefaults;
    meta: { knownProviders: KnownProviders; knownModels: KnownModels };
  }> {
    return this.request(API_ENDPOINTS.ADMIN_PLATFORM_SETTINGS_DEFAULTS);
  }

  /**
   * Update platform defaults
   */
  async updatePlatformDefaults(data: Partial<PlatformDefaults>): Promise<{
    success: boolean;
    data: PlatformDefaults;
  }> {
    return this.request(API_ENDPOINTS.ADMIN_PLATFORM_SETTINGS_DEFAULTS, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * What a retention change would delete: records, coaching notes, coaching
   * sessions, assistant questions and deal states (#2772, #2775).
   *
   * Retention is the one platform setting that destroys data, so the admin UI
   * shows these counts before the change is applied.
   *
   * The counts are scoped to the organizations that INHERIT the platform
   * window: since 2026-08-11 the cleanup cron resolves retention per
   * organization, so an org with its own override is untouched by a platform
   * change and the preview stopped charging the change with its data. That
   * made the counts drop with no visible reason, hence
   * `organizationsWithOwnRetention` — how many orgs the numbers leave out.
   * Absent when `days === 0` (unlimited deletes nothing, so nothing is
   * excluded), which is why it is optional rather than always a number.
   */
  async getRetentionImpact(days: number): Promise<RetentionImpactResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.ADMIN_PLATFORM_SETTINGS_RETENTION_IMPACT(days),
      retentionImpactResponseSchema
    );
  }

  /**
   * Update the platform retention period.
   *
   * Deliberately separate from updatePlatformDefaults: retention used to ride
   * along in that payload and a stale value once wiped ~42k records.
   */
  async updatePlatformRetention(dataRetentionDays: number): Promise<{
    success: boolean;
    data: { dataRetentionDays: number };
  }> {
    return this.request(API_ENDPOINTS.ADMIN_PLATFORM_SETTINGS_RETENTION, {
      method: 'PUT',
      body: JSON.stringify({ dataRetentionDays }),
    });
  }

  /**
   * Update a specific platform setting
   */
  async updatePlatformSetting(
    key: string,
    data: { value: string; description?: string }
  ): Promise<SuccessResponse> {
    return this.request(API_ENDPOINTS.ADMIN_PLATFORM_SETTINGS_BY_KEY(key), {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // ============================================================================
  // HEALTH
  // ============================================================================

  /**
   * Get failed deliveries - validated with Zod
   */
  async getHealthFailedDeliveries(params?: {
    projectId?: string;
    targetType?: 'crm' | 'email' | 'webhook';
    // Backward compatibility for older callers
    project_id?: string;
    target_type?: string;
    limit?: number;
    offset?: number;
  }): Promise<HealthFailedDeliveriesResponse> {
    const searchParams = new URLSearchParams();
    const projectId = params?.projectId ?? params?.project_id;
    const targetType = params?.targetType ?? params?.target_type;

    if (projectId) searchParams.append('projectId', projectId);
    if (targetType) searchParams.append('targetType', targetType);
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.offset) searchParams.append('offset', params.offset.toString());
    const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
    return this.validatedRequest(
      `${API_ENDPOINTS.HEALTH_FAILED_DELIVERIES}${query}`,
      healthFailedDeliveriesResponseSchema
    );
  }

  /**
   * Get health integrations - validated with Zod
   */
  async getHealthIntegrations(): Promise<HealthIntegrationsResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.HEALTH_INTEGRATIONS,
      healthIntegrationsResponseSchema
    );
  }

  /**
   * Retry delivery - validated with Zod. A record whose source has processing
   * paused or withdrawn is refused with 409 (#2709) — an expected answer.
   */
  async retryDelivery(deliveryId: string): Promise<HealthDeliveryRetryResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.HEALTH_DELIVERY_RETRY(deliveryId),
      healthDeliveryRetryResponseSchema,
      { method: 'POST', expectedRefusals: PROCESSING_PAUSED_REFUSALS }
    );
  }
}
